import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getButtonNormalColor } from '@shared/constants/dsm-color-map.constants';
import { PasskeyAction } from '@shared/enums';
import { ThirdPartyProvider } from '@shared/enums/third-party-provider.enum';
import { useThirdPartyProvider } from '@shared/models';
import { useWalletConnectI18n } from '@modules/walletconnect/hooks/useWalletConnectI18n';
import {
  buildPasskeyCallbackUrl,
  buildPasskeyCredentialSuccessCallbackUrl,
} from '@modules/passkey/helpers/passkey-callback.helper';
import { resolvePasskeyCallbackStatus } from '@modules/passkey/helpers/passkey-callback-status.helper';
import { isPasskeyAppClient } from '@modules/passkey/helpers/passkey-client.helper';
import { resolveRegisterHandoff } from '@modules/passkey/helpers/passkey-handoff.helper';
import {
  parseCreationOptionsFromOptionsJson,
  parseRequestOptionsFromOptionsJson,
} from '@modules/passkey/helpers/passkey-options-json.helper';
import {
  consumeAndReplacePendingPasskeyRedirect,
  redirectViaReloadThenReplace,
} from '@modules/passkey/helpers/passkey-pending-redirect.helper';
import { getPasskeySupport } from '@modules/passkey/helpers/passkey-support.helper';
import { createCredentialJson, getCredentialJson } from '@modules/passkey/helpers/webauthn-credential.helper';
import type {
  PasskeyCallbackQuery,
  PasskeyCallbackReason,
} from '@modules/passkey/interfaces/passkey-callback.interface';
import { createPasskeyApiService, getPasskeyApiReason } from '@modules/passkey/services/passkey-api.service';

const PASSKEY_PAGE_STYLE: React.CSSProperties = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#F3F5F7',
  padding: '0 24px',
};

const PASSKEY_COPY_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
};

const PASSKEY_TITLE_STYLE: React.CSSProperties = {
  margin: 0,
  width: '100%',
  fontSize: '20px',
  fontWeight: 600,
  lineHeight: '24px',
  color: '#333333',
  textAlign: 'center',
};

const PASSKEY_DESCRIPTION_STYLE: React.CSSProperties = {
  margin: 0,
  width: '100%',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '18px',
  color: '#959799',
  textAlign: 'center',
};

/**
 * Passkey 操作區（驗證按鈕與返回 App 按鈕）的垂直排版樣式。
 * @external https://innotech.atlassian.net/browse/IN-144601 - 新增操作區容器以同時放置驗證與返回按鈕
 * @external https://innotech.atlassian.net/browse/IN-145048 - 依定稿調整按鈕區間距與寬度
 */
const PASSKEY_ACTIONS_STYLE: React.CSSProperties = {
  marginTop: '20px',
  width: '100%',
  maxWidth: '327px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

/**
 * Passkey「連接密鑰」按鈕基礎樣式；背景色改由 DSM Button/Normal 動態套用。
 * @external https://innotech.atlassian.net/browse/IN-144499 - 移除硬編碼背景色，改套用 DSM Button/Normal
 * @external https://innotech.atlassian.net/browse/IN-144602 - 改由 getButtonNormalColor 注入業主色
 */
const PASSKEY_SSO_BUTTON_STYLE: React.CSSProperties = {
  width: '100%',
  height: '56px',
  padding: '12px',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '20px',
};

/**
 * Passkey「返回 App」按鈕樣式。
 * @external https://innotech.atlassian.net/browse/IN-144601 - 新增返回 App 按鈕樣式
 * @external https://innotech.atlassian.net/browse/IN-145075 - 依定稿調整返回按鈕視覺
 * @external https://innotech.atlassian.net/browse/IN-145048 - 依定稿調整返回按鈕視覺
 */
const PASSKEY_RETURN_BUTTON_STYLE: React.CSSProperties = {
  width: '100%',
  height: '56px',
  padding: '12px',
  backgroundColor: '#FFFFFF',
  color: '#333333',
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '20px',
};

const redirectToCallback = (redirectUrl: string, callbackQuery: PasskeyCallbackQuery) => {
  window.location.replace(buildPasskeyCallbackUrl(redirectUrl, callbackQuery));
};

const redirectToCredentialSuccess = (
  redirectUrl: string,
  callbackQuery: PasskeyCallbackQuery,
  credentialJson: string,
) => {
  window.location.replace(buildPasskeyCredentialSuccessCallbackUrl(redirectUrl, callbackQuery, credentialJson));
};

const redirectWithReason = (
  redirectUrl: string,
  callbackBase: Pick<PasskeyCallbackQuery, 'provider' | 'action' | 'state'>,
  reason: PasskeyCallbackReason,
) => {
  redirectToCallback(redirectUrl, {
    ...callbackBase,
    status: resolvePasskeyCallbackStatus(reason),
    reason,
  });
};

const PasskeyVerification = () => {
  const { payload } = useThirdPartyProvider();
  /**
   * 依執行期業主代號解析的 Button/Normal 色，供連接密鑰按鈕背景使用。
   * @external https://innotech.atlassian.net/browse/IN-144499 - 連接密鑰按鈕改套用 DSM Button/Normal
   * @external https://innotech.atlassian.net/browse/IN-144602 - 以 VENDERID 解析業主色
   */
  const buttonNormalColor = getButtonNormalColor(window._env_?.VENDERID);
  const requiresUserGesture = isPasskeyAppClient(payload.clientId);
  const locale = payload.provider === ThirdPartyProvider.Passkey ? payload.lang : undefined;
  const { t, isLoading: isLocaleLoading } = useWalletConnectI18n(locale);
  const passkeyApiService = useMemo(() => {
    if (payload.provider !== ThirdPartyProvider.Passkey) {
      return createPasskeyApiService();
    }

    return createPasskeyApiService({ lang: payload.lang });
  }, [payload]);
  const [isCeremonyRunning, setIsCeremonyRunning] = useState(false);

  useEffect(() => {
    consumeAndReplacePendingPasskeyRedirect();
  }, []);

  /**
   * 處理使用者取消／拒絕 Passkey：需手勢的 App 客戶端留在頁面可重試；其餘情境直接回調 userRejected。
   * @external https://innotech.atlassian.net/browse/IN-144178 - 取消後不立即 deep-link，讓使用者可再次驗證
   * @external https://innotech.atlassian.net/browse/IN-144601 - 取消後改為顯示返回 App 入口
   * @external https://innotech.atlassian.net/browse/IN-147491 - APP 情境返回按鈕改常駐，取消後僅留頁不切換顯示狀態
   */
  const handleUserRejected = useCallback(
    (redirectUrl: string, callbackBase: Pick<PasskeyCallbackQuery, 'provider' | 'action' | 'state'>) => {
      if (requiresUserGesture) {
        return;
      }

      redirectWithReason(redirectUrl, callbackBase, 'userRejected');
    },
    [requiresUserGesture],
  );

  const runCeremony = useCallback(async () => {
    if (payload.provider !== ThirdPartyProvider.Passkey) {
      return;
    }

    const { action, redirectUrl, state } = payload;
    const callbackBase = {
      provider: 'PASSKEY',
      action,
      state,
    } as const;

    if (!redirectUrl) {
      return;
    }

    const support = getPasskeySupport();
    if (!support.isSupported) {
      redirectWithReason(redirectUrl, callbackBase, support.reason ?? 'unsupportedDevice');
      return;
    }

    setIsCeremonyRunning(true);

    try {
      if (action === PasskeyAction.AddPasskey) {
        const handoff = resolveRegisterHandoff(payload);

        if (!handoff) {
          redirectWithReason(redirectUrl, callbackBase, 'ceremonyHandoffMissing');
          return;
        }

        const credentialJson = await createCredentialJson(parseCreationOptionsFromOptionsJson(handoff.optionsJson));

        if (!credentialJson) {
          handleUserRejected(redirectUrl, callbackBase);
          return;
        }

        redirectToCredentialSuccess(
          redirectUrl,
          {
            ...callbackBase,
            status: 'success',
            ceremonyId: handoff.ceremonyId,
          },
          credentialJson,
        );
        return;
      }

      if (action === PasskeyAction.Login) {
        const beginResponse = await passkeyApiService.beginAuthenticate({});
        const credentialJson = await getCredentialJson(parseRequestOptionsFromOptionsJson(beginResponse.optionsJson));

        if (!credentialJson) {
          handleUserRejected(redirectUrl, callbackBase);
          return;
        }

        redirectToCredentialSuccess(
          redirectUrl,
          {
            ...callbackBase,
            status: 'success',
            ceremonyId: beginResponse.ceremonyId,
          },
          credentialJson,
        );
        return;
      }

      if (action === PasskeyAction.ForgotPasswordVerify) {
        if (!payload.account) {
          redirectWithReason(redirectUrl, callbackBase, 'invalidPayload');
          return;
        }

        const beginResponse = await passkeyApiService.beginAuthenticate({
          account: payload.account,
        });
        const credentialJson = await getCredentialJson(parseRequestOptionsFromOptionsJson(beginResponse.optionsJson));

        if (!credentialJson) {
          handleUserRejected(redirectUrl, callbackBase);
          return;
        }

        await passkeyApiService.verifyForgotPassword({
          ceremonyId: beginResponse.ceremonyId,
          credentialJson,
          account: payload.account,
        });

        redirectToCallback(redirectUrl, {
          ...callbackBase,
          status: 'success',
        });
      }
    } catch (error) {
      const reason = getPasskeyApiReason(error);
      if (reason === 'userRejected') {
        handleUserRejected(redirectUrl, callbackBase);
        return;
      }

      redirectWithReason(redirectUrl, callbackBase, reason);
    } finally {
      setIsCeremonyRunning(false);
    }
  }, [handleUserRejected, passkeyApiService, payload]);

  useEffect(() => {
    if (payload.provider !== ThirdPartyProvider.Passkey || requiresUserGesture) {
      return;
    }

    void runCeremony();
  }, [payload.provider, requiresUserGesture, runCeremony]);

  const handlePasskeyButtonClick = () => {
    void runCeremony();
  };

  /**
   * 點擊「返回 App」：組出 userRejected callback，再以 reload-then-replace 導回 App。
   * @external https://innotech.atlassian.net/browse/IN-144601 - 新增返回 App 按鈕行為
   * @external https://innotech.atlassian.net/browse/IN-144178 - 透過 reload-then-replace 避免 iOS 網址無效
   * @external https://innotech.atlassian.net/browse/IN-147491 - APP 情境常駐顯示，點擊仍走 reload-then-replace
   */
  const handleReturnToAppClick = () => {
    if (payload.provider !== ThirdPartyProvider.Passkey || !payload.redirectUrl) {
      return;
    }

    const callbackUrl = buildPasskeyCallbackUrl(payload.redirectUrl, {
      provider: 'PASSKEY',
      action: payload.action,
      state: payload.state,
      status: resolvePasskeyCallbackStatus('userRejected'),
      reason: 'userRejected',
    });

    redirectViaReloadThenReplace(callbackUrl);
  };

  if (payload.provider === ThirdPartyProvider.Passkey && requiresUserGesture) {
    if (!payload.redirectUrl || isLocaleLoading) {
      return null;
    }

    return (
      <div style={PASSKEY_PAGE_STYLE} data-testid="PasskeySsoPrompt">
        <div style={PASSKEY_COPY_STYLE}>
          <h3 style={PASSKEY_TITLE_STYLE}>{t('authentication.passkey.pleaseConnectPasskey')}</h3>
          <p style={PASSKEY_DESCRIPTION_STYLE}>{t('authentication.passkey.connectPasskeyButtonTips')}</p>
        </div>
        <div style={PASSKEY_ACTIONS_STYLE}>
          <button
            type="button"
            data-testid="PasskeySsoButton"
            onClick={handlePasskeyButtonClick}
            disabled={isCeremonyRunning}
            style={{
              ...PASSKEY_SSO_BUTTON_STYLE,
              backgroundColor: buttonNormalColor,
              opacity: isCeremonyRunning ? 0.6 : 1,
              cursor: isCeremonyRunning ? 'not-allowed' : 'pointer',
            }}>
            {t('authentication.passkey.connectPasskey')}
          </button>
          {/*
           * APP 情境常駐顯示「返回 App」，覆蓋取消／跳轉後無法再觸發顯示的延伸情境。
           * @external https://innotech.atlassian.net/browse/IN-147491 - 需求調整為返回APP按鈕常駐
           */}
          <button
            type="button"
            data-testid="PasskeyReturnToAppButton"
            onClick={handleReturnToAppClick}
            disabled={isCeremonyRunning}
            style={{
              ...PASSKEY_RETURN_BUTTON_STYLE,
              opacity: isCeremonyRunning ? 0.6 : 1,
              cursor: isCeremonyRunning ? 'not-allowed' : 'pointer',
            }}>
            {t('common.text.backToApp')}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default PasskeyVerification;
